import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import {
  X,
  Archive,
  Calendar,
  User,
  Activity,
  Users,
  TrendingUp,
  Download,
  FileText,
  Clock,
  CheckCircle,
  Award
} from 'lucide-react';
import { enqueueSnackbar } from 'notistack';
import { GrupoFechamento } from '../../types/grupos';
import { fechamentosService } from '../../services/fechamentos';

interface VisualizarFechamentoModalProps {
  isOpen: boolean;
  onClose: () => void;
  fechamentoId: string | null;
}

export const VisualizarFechamentoModal: React.FC<VisualizarFechamentoModalProps> = ({
  isOpen,
  onClose,
  fechamentoId
}) => {
  const [fechamento, setFechamento] = useState<GrupoFechamento | null>(null);
  const [loading, setLoading] = useState(false);

  const loadFechamento = async () => {
    if (!fechamentoId) return;

    setLoading(true);
    try {
      const data = await fechamentosService.obterFechamento(fechamentoId);
      setFechamento(data);
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || 'Erro ao carregar fechamento',
        { variant: 'error' }
      );
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleExportarDados = () => {
    if (!fechamento) return;

    const dados = {
      informacoes_gerais: {
        id: fechamento.id,
        grupo: fechamento.grupo.grupo,
        periodo: {
          inicio: fechamento.data_inicio,
          fim: fechamento.data_fim
        },
        criado_por: fechamento.militarCriador?.nick || 'N/A',
        data_fechamento: fechamento.data_fechamento,
        status: fechamento.status,
        observacoes: fechamento.observacoes
      },
      estatisticas: {
        total_acoes: fechamento.total_acoes,
        total_membros: fechamento.total_membros,
        acoes_por_tipo: fechamento.snapshot_dados.estatisticas.acoes_por_tipo,
        acoes_por_militar: fechamento.snapshot_dados.estatisticas.acoes_por_militar
      },
      dados_completos: fechamento.snapshot_dados
    };

    const dataStr = JSON.stringify(dados, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);

    const exportFileDefaultName = `fechamento_${fechamento.grupo.grupo}_${new Date(fechamento.data_inicio).toISOString().split('T')[0]}_${new Date(fechamento.data_fim).toISOString().split('T')[0]}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();

    enqueueSnackbar('Dados exportados com sucesso!', { variant: 'success' });
  };

  useEffect(() => {
    if (isOpen && fechamentoId) {
      loadFechamento();
    } else {
      setFechamento(null);
    }
  }, [isOpen, fechamentoId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <Card className="w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5 text-purple-600" />
              Detalhes do Fechamento
            </CardTitle>
            <CardDescription>
              Visualização completa do snapshot das ações aprovadas do grupo
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-6 w-6 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <span className="ml-2 text-muted-foreground">Carregando fechamento...</span>
            </div>
          ) : fechamento ? (
            <div className="space-y-6">
              {/* Informações Gerais */}
              <Card className="border-l-4 border-l-purple-500">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg text-purple-700 dark:text-purple-400">Informações Gerais</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Grupo:</Label>
                      <p className="text-foreground font-medium">{fechamento.grupo.grupo}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Período:</Label>
                      <p className="text-foreground font-medium">
                        {new Date(fechamento.data_inicio).toLocaleDateString()} - {new Date(fechamento.data_fim).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Criado por:</Label>
                      <p className="text-foreground font-medium">{fechamento.militarCriador?.nick || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Data do Fechamento:</Label>
                      <p className="text-foreground font-medium">
                        {new Date(fechamento.data_fechamento).toLocaleDateString()} às {new Date(fechamento.data_fechamento).toLocaleTimeString()}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Status:</Label>
                      <Badge
                        variant={fechamento.status === 'ativo' ? 'default' : 'secondary'}
                        className={fechamento.status === 'ativo' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' : ''}
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />
                        {fechamento.status}
                      </Badge>
                    </div>
                    {fechamento.observacoes && (
                      <div className="md:col-span-2 lg:col-span-3">
                        <Label className="text-sm font-medium text-muted-foreground">Observações:</Label>
                        <p className="text-foreground mt-1 p-3 bg-muted/50 rounded-md border border-border">{fechamento.observacoes}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Estatísticas Resumidas */}
              <Card className="border-l-4 border-l-blue-500">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg text-blue-700 dark:text-blue-400 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Estatísticas do Período
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 dark:bg-blue-950/30 dark:border-blue-800">
                      <div className="flex items-center gap-2 text-blue-600 mb-2">
                        <Activity className="w-5 h-5" />
                        <span className="text-sm font-medium">Ações Aprovadas</span>
                      </div>
                      <p className="text-3xl font-bold text-blue-800">{fechamento.total_acoes}</p>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg border border-green-200 dark:bg-green-950/30 dark:border-green-800">
                      <div className="flex items-center gap-2 text-green-600 mb-2">
                        <Users className="w-5 h-5" />
                        <span className="text-sm font-medium">Membros Ativos</span>
                      </div>
                      <p className="text-3xl font-bold text-green-800">{fechamento.total_membros}</p>
                    </div>

                    <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 dark:bg-yellow-950/30 dark:border-yellow-800">
                      <div className="flex items-center gap-2 text-yellow-600 mb-2">
                        <TrendingUp className="w-5 h-5" />
                        <span className="text-sm font-medium">Pontuação Total</span>
                      </div>
                      <p className="text-3xl font-bold text-yellow-800">{fechamento.snapshot_dados.estatisticas.pontuacao_total || 0}</p>
                    </div>

                    <div className="bg-orange-50 p-4 rounded-lg border border-orange-200 dark:bg-orange-950/30 dark:border-orange-800">
                      <div className="flex items-center gap-2 text-orange-600 mb-2">
                        <FileText className="w-5 h-5" />
                        <span className="text-sm font-medium">Tipos de Ação</span>
                      </div>
                      <p className="text-3xl font-bold text-orange-800">
                        {Object.keys(fechamento.snapshot_dados.estatisticas.acoes_por_tipo).length}
                      </p>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 dark:bg-purple-950/30 dark:border-purple-800">
                      <div className="flex items-center gap-2 text-purple-600 mb-2">
                        <User className="w-5 h-5" />
                        <span className="text-sm font-medium">Militares Ativos</span>
                      </div>
                      <p className="text-3xl font-bold text-purple-800">
                        {Object.keys(fechamento.snapshot_dados.estatisticas.acoes_por_militar).length}
                      </p>
                    </div>
                  </div>

                  {/* Breakdown por Tipo de Ação */}
                  {Object.keys(fechamento.snapshot_dados.estatisticas.acoes_por_tipo).length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-semibold text-foreground mb-3">Ações por Tipo:</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Object.entries(fechamento.snapshot_dados.estatisticas.acoes_por_tipo).map(([tipo, quantidade]) => (
                          <div key={tipo} className="flex justify-between items-center p-3 bg-muted/50 rounded-lg border border-border">
                            <span className="font-medium text-muted-foreground">{tipo}</span>
                            <Badge variant="outline" className="bg-card">
                              {quantidade} {quantidade === 1 ? 'ação' : 'ações'}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Breakdown por Militar */}
                  {Object.keys(fechamento.snapshot_dados.estatisticas.acoes_por_militar).length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-semibold text-foreground mb-3">Ações por Militar:</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Object.entries(fechamento.snapshot_dados.estatisticas.acoes_por_militar).map(([militar, quantidade]) => (
                          <div key={militar} className="flex justify-between items-center p-3 bg-muted/50 rounded-lg border border-border">
                            <span className="font-medium text-muted-foreground">{militar}</span>
                            <Badge variant="outline" className="bg-card">
                              {quantidade} {quantidade === 1 ? 'ação' : 'ações'}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Pontuação por Tipo de Ação */}
                  {fechamento.snapshot_dados.estatisticas.pontuacao_por_tipo && Object.keys(fechamento.snapshot_dados.estatisticas.pontuacao_por_tipo).length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-yellow-600" />
                        Pontuação por Tipo de Ação:
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Object.entries(fechamento.snapshot_dados.estatisticas.pontuacao_por_tipo)
                          .sort(([, a], [, b]) => (b as number) - (a as number))
                          .map(([tipo, pontos]) => (
                          <div key={tipo} className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg border border-yellow-200 dark:bg-yellow-950/20 dark:border-yellow-800">
                            <span className="font-medium text-muted-foreground">{tipo}</span>
                            <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-700">
                              {pontos} {pontos === 1 ? 'ponto' : 'pontos'}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Pontuação por Militar */}
                  {fechamento.snapshot_dados.estatisticas.pontuacao_por_militar && Object.keys(fechamento.snapshot_dados.estatisticas.pontuacao_por_militar).length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                        <Award className="w-4 h-4 text-green-600" />
                        Pontuação por Militar:
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Object.entries(fechamento.snapshot_dados.estatisticas.pontuacao_por_militar)
                          .sort(([, a], [, b]) => (b as number) - (a as number))
                          .map(([militar, pontos]) => (
                          <div key={militar} className="flex justify-between items-center p-3 bg-green-50 rounded-lg border border-green-200 dark:bg-green-950/20 dark:border-green-800">
                            <span className="font-medium text-muted-foreground">{militar}</span>
                            <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700">
                              {pontos} {pontos === 1 ? 'ponto' : 'pontos'}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Militares Sem Ações */}
              {fechamento.snapshot_dados.estatisticas.militares_sem_acoes && fechamento.snapshot_dados.estatisticas.militares_sem_acoes.length > 0 && (
                <Card className="border-l-4 border-l-red-500">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg text-red-700 dark:text-red-400 flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Militares Sem Ações no Período
                    </CardTitle>
                    <CardDescription>
                      Militares (exceto admins e responsáveis) que não realizaram nenhuma ação aprovada no período
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-red-50 p-4 rounded-lg border border-red-200 mb-4 dark:bg-red-950/20 dark:border-red-800">
                      <div className="flex items-center gap-2 text-red-800 dark:text-red-300 mb-2">
                        <User className="w-4 h-4" />
                        <span className="font-medium">
                          {fechamento.snapshot_dados.estatisticas.militares_sem_acoes.length} militar(es) sem ações
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {fechamento.snapshot_dados.estatisticas.militares_sem_acoes.map((militar, index) => (
                        <div key={index} className="flex items-center gap-3 p-3 bg-card dark:bg-muted/30 rounded-lg border border-red-200 dark:border-red-700">
                          <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-red-600" />
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-foreground">{militar.nick}</div>
                            <div className="text-sm text-muted-foreground">{militar.patente}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Detalhamento das Ações por Militar */}
              {fechamento.snapshot_dados.estatisticas.detalhes_acoes_militar && Object.keys(fechamento.snapshot_dados.estatisticas.detalhes_acoes_militar).length > 0 && (
                <Card className="border-l-4 border-l-indigo-500">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg text-indigo-700 dark:text-indigo-400 flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Detalhamento das Ações Aprovadas por Militar
                    </CardTitle>
                    <CardDescription>
                      Relação detalhada de todas as ações aprovadas realizadas por cada militar no período
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {Object.entries(fechamento.snapshot_dados.estatisticas.detalhes_acoes_militar)
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([militar, acoes]) => (
                        <div key={militar} className="border border-border rounded-lg overflow-hidden bg-background">
                          <div className="bg-indigo-50 px-4 py-3 border-b dark:bg-indigo-950/30 dark:border-border">
                            <div className="flex items-center justify-between">
                              <h5 className="font-semibold text-indigo-800 dark:text-indigo-300 flex items-center gap-2">
                                <User className="w-4 h-4" />
                                {militar}
                              </h5>
                              <div className="flex gap-2">
                                <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700">
                                  {(acoes as any[]).length} {(acoes as any[]).length === 1 ? 'ação' : 'ações'}
                                </Badge>
                                <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700">
                                  {(acoes as any[]).reduce((sum, acao: any) => sum + (acao.pontuacao || 0), 0)} pts
                                </Badge>
                              </div>
                            </div>
                          </div>
                          <div className="p-4">
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead>
                                  <tr className="border-b border-border">
                                    <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                      Tipo de Ação
                                    </th>
                                    <th className="text-center py-2 px-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                      Data
                                    </th>
                                    <th className="text-center py-2 px-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                      Pontuação
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                  {(acoes as any[])
                                    .sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime())
                                    .map((acao: any, index: number) => (
                                    <tr key={index} className="hover:bg-muted/50">
                                      <td className="py-3 px-3">
                                        <div className="font-medium text-foreground">{acao.tipo}</div>
                                      </td>
                                      <td className="py-3 px-3 text-center">
                                        <div className="text-sm text-muted-foreground">
                                          {(() => {
                                            const date = new Date(acao.data);
                                            return isNaN(date.getTime()) ? 'Data inválida' :
                                              `${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
                                          })()}
                                        </div>
                                      </td>
                                      <td className="py-3 px-3 text-center">
                                        <Badge variant="outline" className="bg-yellow-50 text-yellow-800 border-yellow-300 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-700">
                                          {acao.pontuacao || 0} pts
                                        </Badge>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Ações */}
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleExportarDados}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Exportar Dados
                </Button>
                <Button
                  onClick={onClose}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                >
                  Fechar
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Archive className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <p>Fechamento não encontrado</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
